import React, { useState } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Award, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AvaliacaoDialog from '../components/AvaliacaoDialog';
import { mockProfessores } from '../data/mock';

const EquipaDetail = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [isFormOpen, setIsFormOpen] = useState(false);

  // 1) tenta slug ou id dos params; 2) tenta extrair slug do pathname (/ruimarques etc.)
  const pathSlug = location.pathname.replace(/^\/+/, '').split('/')[0];
  const trainer =
    (params.slug && mockProfessores.find(p => p.slug === params.slug)) ||
    (params.id && mockProfessores.find(p => p.id === parseInt(params.id))) ||
    mockProfessores.find(p => p.slug === pathSlug);

  // Voltar inteligente: em mobile vai para o card do professor; em desktop, secção geral.
  const handleVoltar = (e) => {
    e.preventDefault();
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
    const target = isMobile && trainer && trainer.slug
      ? `/equipa#${trainer.slug}`
      : '/equipa#conheca-equipa';
    navigate(target);
  };

  if (!trainer) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center text-white">
          <h1 className="text-4xl mb-4" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal" }}>
            Trainer não encontrado
          </h1>
          <Link to="/equipa">
            <Button className="bg-yellow-400 hover:bg-yellow-500 text-black text-[20px] px-8 py-6" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "-0.02em" }}>
              Voltar para Equipa
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header onOpenForm={() => setIsFormOpen(true)} />
      
      {/* Hero Section with Large Image */}
      <section className="relative pt-20 bg-black">
        <div className="container mx-auto px-0">
          {/* Back Button */}
          <div className="px-6 lg:px-12 pb-6">
            <Link to="/equipa#conheca-equipa" onClick={handleVoltar}>
              <Button variant="ghost" className="text-white hover:text-yellow-400 text-[20px]" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "-0.02em" }}>
                <ArrowLeft className="mr-2" /> Voltar
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Large Hero Image */}
            <div className="relative h-[60vh] lg:h-[80vh] overflow-hidden">
              <img
                src={trainer.foto}
                alt={trainer.nome}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60"></div>
            </div>

            {/* Trainer Info */}
            <div className="bg-black text-white p-8 lg:p-16 flex flex-col justify-center">
              <h1 className="text-5xl md:text-6xl lg:text-7xl mb-4" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "-0.02em" }}>
                {trainer.nome}
              </h1>
              <div className="w-32 h-1 bg-yellow-400 mb-6"></div>
              
              <p className="text-2xl md:text-3xl text-yellow-400 mb-8" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal" }}>
                {trainer.role}
              </p>
              
              <div className="flex items-center space-x-6 mb-10 bg-gray-900 p-6 border-l-4 border-yellow-400">
                <Briefcase className="text-yellow-400" size={32} />
                <div>
                  <p className="text-sm text-gray-400 mb-1" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal" }}>
                    Experiência
                  </p>
                  <p className="text-2xl" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal" }}>
                    {trainer.experiencia}
                  </p>
                </div>
              </div>

              <Button
                onClick={() => setIsFormOpen(true)}
                size="lg"
                className="bg-yellow-400 hover:bg-yellow-500 text-black text-[20px] px-10 py-7 border-2 border-yellow-400 hover:shadow-2xl transform hover:-translate-y-1 transition-all"
                style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "-0.02em" }}
              >
                MARCAR SESSÃO
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Bio Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl text-black mb-6" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "-0.02em" }}>
              SOBRE {trainer.nome.split(' ')[0].toUpperCase()}
            </h2>
            <div className="w-24 h-1 bg-yellow-400 mb-8"></div>
          </div>
          
          {trainer.bioParagraphs ? (
            <div className="space-y-5">
              {trainer.bioParagraphs.map((p, i) => (
                <p
                  key={i}
                  className="text-base md:text-lg text-gray-700 leading-relaxed"
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 300,
                    fontStyle: p.italic ? 'italic' : 'normal',
                    letterSpacing: "0.01em",
                    lineHeight: "1.6"
                  }}
                >
                  {p.italic ? `“${p.text}”` : p.text}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "-0.01em" }}>
              {trainer.bio}
            </p>
          )}
        </div>
      </section>

      {/* Especialidades & Certificações */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto">
            {/* Especialidades */}
            <div className="bg-white border-4 border-black p-8 lg:p-10">
              <div className="flex items-center mb-8 border-b-4 border-yellow-400 pb-4">
                <Award className="text-yellow-400 mr-4" size={32} />
                <h2 className="text-3xl md:text-4xl" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal" }}>
                  Especialidades
                </h2>
              </div>
              <ul className="space-y-4">
                {trainer.especialidades.map((esp, index) => (
                  <li key={index} className="flex items-start">
                    <span className="w-2 h-2 bg-yellow-400 mr-4 mt-2 flex-shrink-0" />
                    <span className="text-lg md:text-xl" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal" }}>
                      {esp}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Certificações */}
            <div className="bg-white border-4 border-black p-8 lg:p-10">
              <div className="flex items-center mb-8 border-b-4 border-yellow-400 pb-4">
                <Award className="text-yellow-400 mr-4" size={32} />
                <h2 className="text-3xl md:text-4xl" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal" }}>
                  Certificações
                </h2>
              </div>
              <div className="space-y-4">
                {trainer.certificacoes.map((cert, index) => (
                  <div key={index} className="border-2 border-yellow-400 px-5 py-4 text-black bg-yellow-50" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal" }}>
                    {cert}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-black text-white">
        <div className="container mx-auto px-6 lg:px-12 text-center max-w-4xl">
          <h2 className="text-4xl md:text-5xl lg:text-6xl mb-8" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "-0.02em" }}>
            Pronto para começar a sua jornada?
          </h2>
          <div className="w-32 h-1 bg-yellow-400 mx-auto mb-8"></div>
          <p className="text-base md:text-lg text-gray-300 mb-10 leading-relaxed max-w-2xl mx-auto" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300, letterSpacing: "0.01em", lineHeight: "1.7" }}>
            Agende uma avaliação física gratuita com {trainer.nome.split(' ')[0]} e descubra como podemos ajudá-lo a alcançar os seus objetivos.
          </p>
          <Button
            onClick={() => setIsFormOpen(true)}
            size="lg"
            className="bg-yellow-400 hover:bg-yellow-500 text-black text-[20px] px-12 py-8 border-2 border-yellow-400 hover:shadow-2xl transform hover:-translate-y-1 transition-all"
            style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "-0.02em" }}>
            MARCAR AVALIAÇÃO FÍSICA GRATUITA
          </Button>
        </div>
      </section>

      <Footer />
      <AvaliacaoDialog isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </div>
  );
};

export default EquipaDetail;
